import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { LibroService } from "service/LibroService";
import ModalLibro from "components/Modals/ModalLibro";
import FSelect from "components/Select/FSelect";
import CustomPagination from "components/Pagination/Pagination";

const libroService = new LibroService();

function Libro() {
  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listLibros, setListLibros] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTipo, setFilterTipo] = useState("Todos");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLibro, setSelectedLibro] = useState(null);
  const [tipoModal, setTipoModal] = useState("Material");

  // Loading state
  const [loadingBuscar, setLoadingBuscar] = useState(false);

  // Confirm modal states
  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ mensaje: "", onConfirm: null });

  const getLibros = async () => {
    setLoadingBuscar(true);
    try {
      const data = await libroService.getLibros();
      const list = Array.isArray(data) ? data : data?.data || [];
      setListLibros(list);
      applyFilters(list, searchQuery, filterTipo);
    } catch (error) {
      console.error("Error cargando libros:", error);
      notify("danger", "Error al cargar los libros");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const applyFilters = (data, query, tipo) => {
    let filtered = data;
    if (tipo !== "Todos") {
      filtered = filtered.filter((item) => item.tipo === tipo);
    }
    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.titulo || "").toLowerCase().includes(q)
      );
    }
    setListFiltered(filtered);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(listLibros, query, filterTipo);
    setCurrentPage(1);
  };

  const handleFilterTipo = (e) => {
    const tipo = e.target.value;
    setFilterTipo(tipo);
    applyFilters(listLibros, searchQuery, tipo);
    setCurrentPage(1);
  };

  const openModal = (tipo, libro = null) => {
    setTipoModal(tipo);
    setSelectedLibro(libro);
    setModalOpen(true);
  };

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setSelectedLibro(null);
  };

  const handleDelete = async (libro) => {
    setConfirmData({
      mensaje: `¿Estás seguro de eliminar "${libro.titulo}"? Esta acción no se puede deshacer.`,
      onConfirm: async () => {
        setConfirmModal(false);
        try {
          const res = await libroService.deleteLibro(libro._id);
          if (res?.confirmation === "success") {
            getLibros();
          } else {
            notify("danger", res?.message || "Error al eliminar");
          }
        } catch (error) {
          console.error("Error eliminando libro:", error);
          notify("danger", "Error al eliminar el libro");
        }
      },
    });
    setConfirmModal(true);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listFiltered.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Libros y Materiales</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      title="Configuración"
                    >
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="8">
                    <FormGroup className="mb-0">
                      <Label for="search">Buscar</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Buscar por título..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="tipo">Tipo</Label>
                      <FSelect
                        id="tipo"
                        value={filterTipo}
                        onChange={handleFilterTipo}
                        options={[
                          { value: "Todos", label: "Todos" },
                          { value: "Material", label: "Material" },
                          { value: "Horario", label: "Horario" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getLibros}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones */}
                <Row className="mb-4">
                  <Col md="6">
                    {/* Espacio reservado para acciones masivas */}
                  </Col>
                  <Col md="6" className="text-right">
                    <Button
                      color="primary"
                      onClick={() => openModal("Material")}
                      className="btn-fill mr-2"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Agregar Material
                    </Button>
                    <Button
                      color="warning"
                      onClick={() => openModal("Horario")}
                      className="btn-fill"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Agregar Horario
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />
                <Table>
                  <thead className="text-primary">
                    <tr>
                      <th>Tomo</th>
                      <th>Título / Descripción</th>
                      <th>Tipo</th>
                      <th>URL</th>
                      <th>Estado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{row.tomo || "-"}</td>
                        <td>{row.titulo}</td>
                        <td>
                          <Badge color={row.tipo === "Horario" ? "warning" : "primary"}>
                            {row.tipo}
                          </Badge>
                        </td>
                        <td>
                          {row.url ? (
                            <a href={row.url} target="_blank" rel="noopener noreferrer">
                              Ver enlace
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {row.estado === "Activo" ? (
                            <span className="text-success">Activo</span>
                          ) : (
                            <span className="text-danger">Inactivo</span>
                          )}
                        </td>
                        <td className="text-right">
                          <Button
                            color="info"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Editar"
                            onClick={() => openModal(row.tipo, row)}
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Eliminar"
                            onClick={() => handleDelete(row)}
                          >
                            <i className="tim-icons icon-trash-simple" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No se encontraron registros
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={setCurrentPage}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <ModalLibro
        isOpen={modalOpen}
        toggle={toggleModal}
        selectedLibro={selectedLibro}
        tipoDefault={tipoModal}
        onSave={getLibros}
      />

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} size="sm" centered>
        <ModalHeader toggle={() => setConfirmModal(false)}>Confirmar acción</ModalHeader>
        <ModalBody><p>{confirmData.mensaje}</p></ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button color="danger" onClick={confirmData.onConfirm}>Confirmar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default Libro;
